import { useEffect, useState, useMemo } from 'react';
import { Activity, Search, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';
import { http } from '@/libs/http';
import { PageLayout } from '@/components/page-layout';

interface ActivityLog {
  activity_id: string;
  resource: string;
  id: string;
  event: string;
  actor: string;
  timestamp: string;
  data: string;
}

interface PaginatedActivities {
  items: ActivityLog[];
  total: number;
  page: number;
  page_size: number;
}

export const ActivitiesPage = () => {
  const { setBreadcrumbs } = useLayout();

  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // Search and Filter states
  const [searchText, setSearchText] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [serverSearch, setServerSearch] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');

  const fetchActivities = async (searchVal: string, eventVal: string, resourceVal: string, pageNum: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pageNum.toString());
      params.append('page_size', '50');
      if (searchVal) params.append('search', searchVal);
      if (eventVal) params.append('event', eventVal);
      if (resourceVal) params.append('resource', resourceVal);

      const res = await http.get<PaginatedActivities>(`/mgmt/v1/activities?${params.toString()}`);
      setActivities(res.items || []);
      setTotal(res.total || 0);
    } catch (e: any) {
      console.error(e);
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setBreadcrumbs([{ name: 'Activities' }]);
    fetchActivities(serverSearch, eventFilter, resourceFilter, page);
  }, [setBreadcrumbs, page]);

  // Handle typing search text (triggers client filtering immediately)
  const handleTextChange = (value: string) => {
    setSearchText(value);
    setClientSearch(value);
  };

  // Handle search database button click (runs server search query)
  const handleServerSearch = () => {
    setServerSearch(searchText);
    setPage(1);
    fetchActivities(searchText, eventFilter, resourceFilter, 1);
  };

  // Handle dropdown filters
  const handleEventFilterChange = (value: string) => {
    setEventFilter(value);
    setPage(1);
    fetchActivities(serverSearch, value, resourceFilter, 1);
  };

  const handleResourceFilterChange = (value: string) => {
    setResourceFilter(value);
    setPage(1);
    fetchActivities(serverSearch, eventFilter, value, 1);
  };

  // Client-side search filtering applied on top of fetched items
  const filteredActivities = useMemo(() => {
    if (!clientSearch.trim()) return activities;
    const query = clientSearch.toLowerCase().trim();
    return activities.filter((act) => {
      const parsedData = (() => {
        try {
          return act.data ? JSON.stringify(JSON.parse(act.data)).toLowerCase() : '';
        } catch {
          return act.data ? act.data.toLowerCase() : '';
        }
      })();

      return (
        act.actor.toLowerCase().includes(query) ||
        act.event.toLowerCase().includes(query) ||
        act.resource.toLowerCase().includes(query) ||
        act.id.toLowerCase().includes(query) ||
        parsedData.includes(query)
      );
    });
  }, [activities, clientSearch]);

  const totalPages = Math.max(1, Math.ceil(total / 50));

  const formatData = (dataStr: string) => {
    try {
      const parsed = JSON.parse(dataStr);
      if (parsed.tenant && parsed.comment) {
        return (
          <div className="space-y-0.5">
            <div className="text-xs text-slate-800 dark:text-slate-200">
              <span className="font-bold">Tenant:</span> {parsed.tenant}
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 italic">
              "{parsed.comment}"
            </div>
          </div>
        );
      }
      return <pre className="text-[11px] max-w-xs overflow-auto font-mono text-slate-500">{JSON.stringify(parsed, null, 2)}</pre>;
    } catch {
      return <span className="text-slate-500 text-xs">{dataStr}</span>;
    }
  };

  return (
    <PageLayout title="Activities" description="View security and configuration audit logs.">
      <div className="bg-white dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-900 overflow-hidden">
        {/* Filters and Search Bar */}
        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-900 flex flex-col xl:flex-row xl:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row flex-1 gap-4 max-w-xl">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search or press Enter to search database..."
                value={searchText}
                onChange={(e) => handleTextChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleServerSearch()}
                className="w-full pl-10 pr-20 py-2 border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-(--primary)/20 focus:border-(--primary) transition-all"
              />
              <button
                onClick={handleServerSearch}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-1 hover:bg-(--primary) text-white text-xs font-bold rounded-lg bg-(--primary-dark) transition-colors"
              >
                Search
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Event Filter */}
            <div className="flex items-center gap-1.5">
              <Filter className="w-4 h-4 text-slate-400 shrink-0" />
              <div className="flex bg-slate-100 dark:bg-slate-900 p-0.5 rounded-xl gap-0.5">
                {(['', 'USER_APPROVED', 'USER_REJECTED'] as const).map(val => (
                  <button
                    key={val}
                    onClick={() => handleEventFilterChange(val)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 ${
                      eventFilter === val
                        ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                    }`}
                  >
                    {val === '' ? 'All Events' : val.replace('USER_', '')}
                  </button>
                ))}
              </div>
            </div>

            {/* Resource Filter */}
            <div className="flex bg-slate-100 dark:bg-slate-900 p-0.5 rounded-xl gap-0.5">
              {(['', 'user'] as const).map(val => (
                <button
                  key={val}
                  onClick={() => handleResourceFilterChange(val)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all duration-150 ${
                    resourceFilter === val
                      ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm'
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                  }`}
                >
                  {val === '' ? 'All Resources' : val}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Table View */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-12 flex justify-center items-center">
              <Loader />
            </div>
          ) : filteredActivities.length > 0 ? (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-white dark:bg-slate-950 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100 dark:border-slate-900">
                  <th className="px-6 py-3 whitespace-nowrap">Resource & ID</th>
                  <th className="px-6 py-3 whitespace-nowrap">Event</th>
                  <th className="px-6 py-3 whitespace-nowrap">Actor</th>
                  <th className="px-6 py-3 whitespace-nowrap">Timestamp</th>
                  <th className="px-6 py-3 whitespace-nowrap">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100/60 dark:divide-slate-800/60">
                {filteredActivities.map((act) => (
                  <tr
                    key={act.activity_id}
                    className="hover:bg-slate-50/50 dark:hover:bg-slate-900/50 transition-colors"
                  >
                    <td className="px-6 py-3 whitespace-nowrap">
                      <div>
                        <span className="text-[10px] bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 px-2 py-0.5 rounded-md uppercase font-bold tracking-wider mr-2">
                          {act.resource}
                        </span>
                        <span className="font-mono text-xs text-slate-500">{act.id}</span>
                      </div>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      <span className={`text-[11px] px-2.5 py-1 rounded-lg font-bold ${
                        act.event === 'USER_APPROVED'
                          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400'
                          : 'bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-400'
                        }`}>
                        {act.event}
                      </span>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                      {act.actor}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-xs text-slate-400 dark:text-slate-500">
                      {new Date(act.timestamp).toLocaleString()}
                    </td>
                    <td className="px-6 py-3 text-xs text-slate-700 dark:text-slate-300">
                      {formatData(act.data)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-12 text-center">
              <Activity className="w-12 h-12 text-slate-300 dark:text-slate-700 mx-auto mb-3" />
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">No activity logs found</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Try adjusting your filters or search terms.</p>
            </div>
          )}
        </div>

        {/* Pagination Section */}
        <div className="px-5 py-3.5 border-t border-slate-100 dark:border-slate-900 flex items-center justify-between text-sm text-slate-500 dark:text-slate-400">
          <div>
            Showing <span className="font-bold text-slate-800 dark:text-slate-200">{filteredActivities.length}</span> of{' '}
            <span className="font-bold text-slate-800 dark:text-slate-200">{total}</span> records
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 border border-slate-200 dark:border-slate-800 rounded-full hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs font-medium">
              Page <span className="font-bold text-slate-800 dark:text-slate-200">{page}</span> of{' '}
              <span className="font-bold text-slate-800 dark:text-slate-200">{totalPages}</span>
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 border border-slate-200 dark:border-slate-800 rounded-full hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
